import { useCallback, useRef, useState } from 'react'
import { useEventChannel } from '@/hooks/useEventChannel'
import { loadViewerPayload, type ViewerCamera, type ViewerEvent, type ViewerPayload, type ViewerRoute } from '@/lib/comrades/viewerPayload'

/** A change the watch screen takes from the event's public channel (docs/REALTIME.md), its `data` as the server sent it. */
export type ViewerChange =
  | { type: 'route.updated'; data: ViewerRoute }
  | { type: 'camera.created' | 'camera.updated'; data: ViewerCamera }
  | { type: 'camera.deleted'; data: { id: string } }
  | { type: 'event.updated'; data: Pick<ViewerEvent, 'id'> & Partial<ViewerEvent> }

const CHANGES = ['route.updated', 'camera.created', 'camera.updated', 'camera.deleted', 'event.updated'] as const

/** What the page takes of the event itself from a message or a fresh load: what its header shows (its logo, and whether
 *  it leads the header). The rest of the event stays as loaded. */
const EVENT_FIELDS = ['logo_url', 'logo_in_header'] as const

/** The payload with what `fresh` says of the event's own fields; the same payload when it says the same. */
function withEvent(payload: ViewerPayload, fresh: Partial<ViewerEvent> | undefined): ViewerPayload {
  if (!fresh || !payload.event) return payload
  const changed = EVENT_FIELDS.filter((field) => field in fresh && fresh[field] !== payload.event[field])
  if (changed.length === 0) return payload
  return { ...payload, event: { ...payload.event, ...Object.fromEntries(changed.map((field) => [field, fresh[field]])) } }
}

/** A camera's stream id: what `camera.deleted` names, and what a camera is matched by. */
const cameraId = (camera: Pick<ViewerCamera, 'id' | 'stream_id'>) => String(camera.id ?? camera.stream_id)

/** Two copies of a camera say the same, so the loaded one keeps its identity (and the map's follow ease doesn't re-run). */
function sameCamera(a: ViewerCamera, b: ViewerCamera): boolean {
  const fields = new Set([...Object.keys(a), ...Object.keys(b)] as (keyof ViewerCamera)[])
  return [...fields].every((field) => JSON.stringify(a[field] ?? null) === JSON.stringify(b[field] ?? null))
}

/** The loaded course with a newer copy's world, and nothing else, so its line and bounds keep their identity. */
function withWorld(route: ViewerRoute, fresh: ViewerRoute[]): ViewerRoute {
  const next = fresh.find((each) => each.id === route.id)
  if (!next || (next.world_url === route.world_url && next.world_status === route.world_status)) return route
  return { ...route, world_url: next.world_url ?? null, world_status: next.world_status ?? null }
}

function withWorlds(payload: ViewerPayload, fresh: ViewerRoute[]): ViewerPayload {
  const route = payload.route && withWorld(payload.route, fresh)
  const routes = payload.routes?.map((each) => withWorld(each, fresh))
  const same = route === payload.route && (routes ?? []).every((each, index) => each === payload.routes?.[index])
  return same ? payload : { ...payload, route, routes }
}

/**
 * The payload with these cameras; the same payload when they say what its own do, each unchanged camera keeping its
 * identity. A featured camera that left is featured no more, as the API's payload only features a public camera.
 */
function withCameras(payload: ViewerPayload, cameras: ViewerCamera[]): ViewerPayload {
  const loaded = new Map(payload.cameras.map((camera) => [cameraId(camera), camera]))
  const next = cameras.map((camera) => {
    const known = loaded.get(cameraId(camera))
    return known && sameCamera(known, camera) ? known : camera
  })
  if (next.length === payload.cameras.length && next.every((camera, index) => camera === payload.cameras[index])) return payload
  const featured = payload.featured_participant_identity
  const featuredStays = featured === null || next.some((camera) => camera.participant_identity === featured)
  return { ...payload, cameras: next, featured_participant_identity: featuredStays ? featured : null }
}

/**
 * The payload with one change from the event's public channel: a course's new world (`route.updated`: only its
 * `world_url` and `world_status`), a camera added or changed in place (`camera.created`, `camera.updated`: matched by
 * its id, a new one after the others), or a camera gone (`camera.deleted`, and a camera no longer public). The same
 * payload when nothing changed.
 */
export function withViewerChange(payload: ViewerPayload, change: ViewerChange): ViewerPayload {
  if (change.type === 'route.updated') return withWorlds(payload, [change.data])
  if (change.type === 'event.updated') return change.data.id === payload.event?.id ? withEvent(payload, change.data) : payload
  const id = cameraId(change.data as ViewerCamera)
  const others = payload.cameras.filter((camera) => cameraId(camera) !== id)
  if (change.type === 'camera.deleted' || (change.data as ViewerCamera).publicly_visible === false) return withCameras(payload, others)
  const camera = change.data as ViewerCamera
  const at = payload.cameras.findIndex((each) => cameraId(each) === id)
  return withCameras(payload, at === -1 ? [...payload.cameras, camera] : payload.cameras.map((each, index) => (index === at ? camera : each)))
}

/** The payload with what a fresh load of it says of the courses' worlds, the cameras and the event's header. */
export function withFreshPayload(payload: ViewerPayload, fresh: ViewerPayload): ViewerPayload {
  const courses = withWorlds(payload, [...(fresh.route ? [fresh.route] : []), ...(fresh.routes ?? [])])
  return withEvent(withCameras(courses, fresh.cameras ?? courses.cameras), fresh.event)
}

/**
 * The viewer payload as the event's public channel keeps it current (docs/REALTIME.md), so an open watch screen needs
 * no reload: a course's rebuilt world swaps in (`route.updated`), a camera added, changed, hidden or removed
 * shows, changes or goes (`camera.created`, `camera.updated`, `camera.deleted`), and the event's logo comes into the
 * header or leaves it (`event.updated`: its `logo_url` and `logo_in_header`). After the socket drops, the payload
 * loads again for what was missed, and the messages that arrive while it loads are applied over it, so a load that
 * read before a change doesn't undo it. The same payload comes back while nothing in it changed; a new one from the
 * page starts over from it.
 */
export function useLiveViewerPayload(eventId: string, payload: ViewerPayload): ViewerPayload {
  const [live, setLive] = useState<{ loaded: ViewerPayload; now: ViewerPayload } | null>(null)
  const current = live?.loaded === payload ? live.now : payload
  // The changes heard while each resync load is on its way, to apply over it when it lands.
  const replays = useRef(new Set<ViewerChange[]>())

  const update = useCallback(
    (change: (now: ViewerPayload) => ViewerPayload) =>
      setLive((state) => {
        const now = state?.loaded === payload ? state.now : payload
        const next = change(now)
        return next === now ? state : { loaded: payload, now: next }
      }),
    [payload],
  )

  const on = Object.fromEntries(
    CHANGES.map((type) => [
      type,
      (data: unknown) => {
        const change = { type, data } as ViewerChange
        for (const replay of replays.current) replay.push(change)
        update((now) => withViewerChange(now, change))
      },
    ]),
  )

  useEventChannel(eventId, {
    scope: 'public',
    on,
    onResync: () => {
      const replay: ViewerChange[] = []
      replays.current.add(replay)
      loadViewerPayload(eventId).then(
        (fresh) => {
          replays.current.delete(replay)
          update((now) => replay.reduce(withViewerChange, withFreshPayload(now, fresh)))
        },
        () => replays.current.delete(replay),
      )
    },
  })

  return current
}
